import { Button, Container, Group, Stack, Text, Title } from '@mantine/core';
import { useRouteError } from 'react-router-dom';

/**
 * Fallback screen rendered at two different moments in `router.tsx`, and they
 * must not read as the same page:
 *
 * 1. As the top-level catch-all (`{ path: '*', element: <NotFound /> }`,
 *    `router.tsx:842`) — a genuine 404: the URL matched nothing.
 * 2. As the root `/` route's `errorElement` — the URL DID match a real route,
 *    and something threw during render. This branch deliberately does not try
 *    to distinguish *why*: whatever the cause, the page is real, so telling the
 *    user "that route doesn't exist" is false — and saying it cost real
 *    diagnosis time on the live incident that prompted this fix (vendor#585),
 *    because `/reports` is a defined route.
 *
 *    **Narrowed 2026-08-18 (vendor#586).** It used to catch a render error from
 *    ANY matched route, which is exactly why one bad page replaced the whole
 *    screen — nav rail included. A pathless boundary one level below the shell
 *    (`RouteError`) takes child-route errors now, so the rail survives them.
 *    Case 2 here is reached only when `RequireAuth`, `RequireSubscription` or
 *    `DesktopLayout` ITSELF throws — where there is no shell left to preserve.
 *    `lib/lazyWithReload.ts` exhausting its chunk-load recovery can still land
 *    here when the failing chunk is one of those (see that file's docblock for
 *    what is and is not confirmed about *why* a chunk import fails).
 *
 * `useRouteError()` is how the two are told apart — per React Router, it is
 * intended to be called from an `errorElement`/`ErrorBoundary`. **It does not
 * return `undefined` for "no active error" — it returns `null`.** Verified
 * against the installed `react-router@6.30.3` source
 * (`RouteErrorContext = React.createContext(null)`, and
 * `RenderErrorBoundary.render()` only mounts a `RouteErrorContext.Provider`
 * at all when `this.state.error !== undefined`, so the plain, non-crashed
 * render path never gets a Provider and falls through to that `null`
 * default). Checking `!== undefined` instead of `!= null` was a real bug
 * caught by this file's own test — it made every render, including the
 * genuine catch-all, read as a crash. Note this hook requires a *data*
 * router context (`createMemoryRouter`/`createBrowserRouter` +
 * `RouterProvider`, which is what `router.tsx` uses) — it throws under a bare
 * `<MemoryRouter>`.
 *
 * Shell: any (this is the root-level fallback outside all three shells; it
 * has no `useSession`/`useUi` dependency and no shell-scoped data hook).
 *
 * Both escape actions below are **real document navigations** (`<a href>` via
 * Mantine's `component="a"`), never a React Router `<Link>`. That is still
 * right, but **the reason recorded here was wrong — corrected 2026-08-18
 * (vendor#586).**
 *
 * This block used to say: *"React Router does not clear an active error
 * boundary on a same-tree client-side navigation, so a `<Link to="/">`
 * rendered by the boundary that is currently showing is inert — clicking it
 * does nothing."* It is not inert. `RenderErrorBoundary.getDerivedStateFromProps`
 * in the installed `react-router@6.30.3` clears the error whenever
 * `state.location !== props.location`, with a comment saying so in as many
 * words. Asserted behaviourally too, on the exact scenario the old claim
 * described — a `<Link>` rendered BY the showing boundary — in
 * `__tests__/RouteError.test.tsx`: it navigates and the boundary clears.
 *
 * The **anchor is still correct**, for the cause that actually reached
 * production rather than the one written down: a chunk-load failure
 * (`lib/lazyWithReload.ts` exhausting its recovery). A client-side hop to
 * another lazily-loaded route can fail exactly the same way, so a full page
 * load is the escape that always works. What is not claimed is that the live
 * incident would have been fixed by a `<Link>` — it would not.
 *
 * Why the distinction is worth the words: it decides whether keeping the nav
 * rail alive during an error is useful. It is — see `RouteError`, which is
 * where a child-route error now stops (vendor#586). If a client-side
 * navigation really could not clear a boundary, a surviving rail would be
 * decoration.
 */
export function NotFound() {
  const error = useRouteError();
  const isRouteCrash = error !== null && error !== undefined;

  return (
    <Container size="sm" py="xl">
      <Stack align="center" gap="md">
        <Title order={1}>{isRouteCrash ? "We couldn't load this page" : 'Not found'}</Title>
        <Text c="dimmed" ta="center">
          {isRouteCrash
            ? 'It may have been updated — reload to continue.'
            : "That route doesn't exist."}
        </Text>
        <Group gap="sm">
          {isRouteCrash && (
            <Button component="a" href={`${window.location.pathname}${window.location.search}`}>
              Reload
            </Button>
          )}
          <Button component="a" href="/" variant={isRouteCrash ? 'default' : 'filled'}>
            Back to dashboard
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
